// Helper functions for image cropping

/**
 * Creates a cropped version of an image file
 * @param file The original image file
 * @param cropData The crop coordinates and dimensions
 * @returns A Promise that resolves to the cropped File
 */
export const cropImageFile = async (
  file: File,
  cropData: { x: number; y: number; width: number; height: number },
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.src = URL.createObjectURL(file)
    img.crossOrigin = "anonymous"

    img.onload = () => {
      URL.revokeObjectURL(img.src)
      const canvas = document.createElement("canvas")
      const ctx = canvas.getContext("2d")

      if (!ctx) {
        reject(new Error("Could not get canvas context"))
        return
      }

      canvas.width = cropData.width
      canvas.height = cropData.height

      ctx.drawImage(img, cropData.x, cropData.y, cropData.width, cropData.height, 0, 0, cropData.width, cropData.height)

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Could not create blob from canvas"))
            return
          }

          const newFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now(),
          })

          resolve(newFile)
        },
        file.type,
        0.9,
      )
    }

    img.onerror = () => {
      URL.revokeObjectURL(img.src)
      reject(new Error("Error loading image"))
    }
  })
}

/**
 * Creates a resized version of an image file
 * @param file The original image file
 * @param percentage The resize percentage (1-100)
 * @returns A Promise that resolves to the resized File
 */
export const resizeImageFile = async (file: File, percentage: number): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.src = URL.createObjectURL(file)
    img.crossOrigin = "anonymous"

    img.onload = () => {
      URL.revokeObjectURL(img.src)
      const canvas = document.createElement("canvas")
      const ctx = canvas.getContext("2d")

      if (!ctx) {
        reject(new Error("Could not get canvas context"))
        return
      }

      const newWidth = img.width * (percentage / 100)
      const newHeight = img.height * (percentage / 100)

      canvas.width = newWidth
      canvas.height = newHeight

      ctx.drawImage(img, 0, 0, newWidth, newHeight)

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Could not create blob from canvas"))
            return
          }

          const newFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now(),
          })

          resolve(newFile)
        },
        file.type,
        0.9,
      )
    }

    img.onerror = () => {
      URL.revokeObjectURL(img.src)
      reject(new Error("Error loading image"))
    }
  })
}
